"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
/* eslint-disable max-lines */
const sha256_1 = __importDefault(require("crypto-js/sha256"));
const rxjs_1 = require("rxjs");
const auth_events_1 = __importDefault(require("./auth.events"));
const debounce_1 = __importDefault(require("./utils/debounce"));
const event_agent_1 = require("./utils/event.agent");
const AUTH_DEFAULT_ROUTE = '/';
const AUTH_REDIRECT_STORAGE = 'AUTH_REDIRECT_STORAGE';
const AUTH_STORAGE = 'cas-js:user';
const BOOSTRAP_CONFIG_DEFAULT_OPTIONS = {
    appName: '',
    onRedirect: (url) => window.location.href = url,
    signinRedirect: true,
    revalidateOnFocus: true,
    revalidateOnReconnect: true
};
class Bootstrap {
    constructor(authService, config) {
        this.config = Object.assign(Object.assign({}, BOOSTRAP_CONFIG_DEFAULT_OPTIONS), config);
        this.authService = authService;
        this.subject = new rxjs_1.Subject();
        this.subscribe = this.subject.subscribe.bind(this.subject);
        this.unsubscribe = this.subject.unsubscribe.bind(this.subject);
        this.authRenewRoute = new URL(this.authService.userManager.settings.silent_redirect_uri).pathname;
        this.authLoginRoute = new URL(this.authService.userManager.settings.redirect_uri).pathname;
        this.authLogoutRoute = new URL(this.authService.userManager.settings.post_logout_redirect_uri).pathname;
        this.authRoutes = [this.authRenewRoute, this.authLoginRoute, this.authLogoutRoute];
        this.checkUser = debounce_1.default((args) => __awaiter(this, void 0, void 0, function* () {
            try {
                yield this._checkUser(args);
            }
            catch (error) {
                console.error('[DEBOUNCE_ERROR]', error);
                throw error;
            }
        }));
        this.accessTokenExpiringListener = () => auth_events_1.default.dispatchRenew();
        this.silentRenewErrorListener = () => auth_events_1.default.dispatchRenewError();
        this.userLoadedListener = () => {
            if (location.pathname === this.authLoginRoute)
                this.getUser();
            else
                this.authenticate();
        };
        this.visibilityChangeListener = () => {
            if (document.visibilityState === 'visible') {
                this.authenticate();
            }
        };
    }
    start(newConfig = {}) {
        try {
            this.config = Object.assign(Object.assign({}, this.config), newConfig);
            this.authenticate();
            if (this.authService.userManager.settings.automaticSilentRenew) {
                this.authService.userManager.startSilentRenew();
                this.authService.userManager.events.addSilentRenewError(this.silentRenewErrorListener);
            }
            this.authService.userManager.events.addAccessTokenExpiring(this.accessTokenExpiringListener);
            this.authService.userManager.events.addUserLoaded(this.userLoadedListener);
            if (this.config.revalidateOnReconnect) {
                window.addEventListener('online', this.authenticate);
            }
            if (this.config.revalidateOnFocus) {
                document.addEventListener('visibilitychange', this.visibilityChangeListener);
            }
        }
        catch (error) {
            this.subject.error({ origin: '[CAS start]', error });
        }
    }
    stop() {
        try {
            if (this.authService.userManager.settings.automaticSilentRenew) {
                this.authService.userManager.stopSilentRenew();
                this.authService.userManager.events.removeSilentRenewError(this.silentRenewErrorListener);
            }
            this.authService.userManager.events.removeAccessTokenExpiring(this.accessTokenExpiringListener);
            this.authService.userManager.events.removeUserLoaded(this.userLoadedListener);
            if (this.config.revalidateOnReconnect) {
                window.removeEventListener('online', this.authenticate);
            }
            if (this.config.revalidateOnFocus) {
                document.removeEventListener('visibilitychange', this.visibilityChangeListener);
            }
        }
        catch (error) {
            this.subject.error({ origin: '[CAS stop]', error });
        }
    }
    authenticate() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                if (new URLSearchParams(location.search).get('error') === 'login_required') {
                    this.logout();
                    return;
                }
                if (location.pathname === this.authRenewRoute || location.pathname === this.authLoginRoute) {
                    yield this.authService.signinCallback().catch(() => this.signinRedirect());
                }
                if (location.pathname === this.authLogoutRoute) {
                    this.authService.isSignoutSilent() ? yield this.authService.signoutSilentCallback()
                        : yield this.authService.signoutCallback();
                    const redirectRoute = sessionStorage.getItem(AUTH_REDIRECT_STORAGE);
                    this.config.onRedirect(redirectRoute || AUTH_DEFAULT_ROUTE);
                    return;
                }
                if (this.authRoutes.indexOf(location.pathname) >= 0) {
                    const redirectRoute = sessionStorage.getItem(AUTH_REDIRECT_STORAGE);
                    this.config.onRedirect(redirectRoute || AUTH_DEFAULT_ROUTE);
                    sessionStorage.removeItem(AUTH_REDIRECT_STORAGE);
                    return;
                }
                this.getUser();
            }
            catch (error) {
                this.subject.error({ origin: '[CAS authenticate]', error });
            }
        });
    }
    signinSilent() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                auth_events_1.default.dispatchRenew();
                const user = yield this.authService.signinSilent();
                return user;
            }
            catch (error) {
                this.signinRedirect();
                this.subject.error({ origin: '[CAS signinSilent]', error });
                return null;
            }
        });
    }
    signinRedirect() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                if (!this.config.signinRedirect)
                    return;
                const currRoute = this.getCurrentRoute();
                localStorage.removeItem(AUTH_STORAGE);
                sessionStorage.setItem(AUTH_REDIRECT_STORAGE, currRoute);
                yield this.authService.signinRedirect();
            }
            catch (error) {
                this.subject.error({ origin: '[CAS signinRedirect]', error });
            }
        });
    }
    /**
     * @description Logs out the current user silently (without redirect).
     * @param {SignoutRequestArgs} args The targeted id_token_hint to be logged out.
     */
    signoutSilent(args) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.authService.signoutSilent(args);
        });
    }
    getUser() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                let user = yield this.authService.getUser();
                if (!user)
                    yield this.signinRedirect();
                if ((user === null || user === void 0 ? void 0 : user.expired) || (!user && !this.config.signinRedirect))
                    user = yield this.signinSilent();
                if (user)
                    localStorage.setItem(AUTH_STORAGE, JSON.stringify(user));
                this.subject.next(user);
                auth_events_1.default.dispatchUser(user);
                return user;
            }
            catch (error) {
                this.subject.error({ origin: '[CAS getUser]', error });
            }
        });
    }
    /**
     * @description Performs an attempt to reissue a new access token for the current user session by
     * performing the sign-in silent process. If this process doesn't succeed, the default sign-out is started.
     * @param {ICheckUser} args Info about the sign-out reason.
     */
    checkSigninSilent(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const user = yield this.signinSilent();
            if (user) {
                this.subject.next(user);
                throw {
                    origin: '[CHECK_SIGNIN_SILENT]',
                    details: {
                        message: 'Silent renew - The current session is still valid'
                    }
                };
            }
            else {
                // eslint-disable-next-line no-console
                console.log({ origin: '[CHECK_SIGNIN_SILENT]', details: Object.assign(Object.assign({}, args), { message: 'Expired session' }) });
                yield this.logout();
            }
        });
    }
    /**
     * @description Sign-out process intended for automatic triggers, generally started by a 401 status.
     * The sign-out will be carried out only if the current session is no longer valid.
     * @param {ICheckUser} args Info about the sign-out reason.
     */
    _checkUser(args) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const user = yield this.authService.getUser();
                if (user === null || user === void 0 ? void 0 : user.access_token) {
                    const userInfoEndpoint = yield this.authService.userManager.metadataService.getUserInfoEndpoint();
                    const response = yield fetch(userInfoEndpoint, {
                        headers: {
                            Authorization: `Bearer ${user.access_token}`,
                            AppOrigin: args.appOrigin || `cas-js internal - ${window.parent.location.origin}`,
                            ApiOrigin: args.origin || ''
                        },
                        method: 'GET',
                        mode: 'cors'
                    });
                    /**
                     * An error might happen, perhaps due to an expired or signed out token.
                     * However, the current session may be still valid. This way, an sign in silent process is performed
                     * attempting to restablish the user's session.
                     */
                    if (response.status < 200 || response.status >= 300) {
                        const data = yield response.json();
                        // eslint-disable-next-line no-console
                        console.log({ origin: '[CHECK_USER]', details: Object.assign(Object.assign({}, args), { message: data }) });
                        // eslint-disable-next-line no-console
                        console.log({ origin: '[CHECK_USER]', details: 'Profile - invalid token' });
                        return this.checkSigninSilent(args);
                    }
                    throw {
                        origin: '[CHECK_USER]',
                        details: {
                            message: 'Profile - The current session is still valid'
                        }
                    };
                }
                else {
                    yield this.checkSigninSilent(args);
                }
            }
            catch (error) {
                console.error(error, args);
                if (error.details) {
                    throw args;
                }
                this.logout();
            }
        });
    }
    logout(error) {
        return __awaiter(this, void 0, void 0, function* () {
            const reason = error ? event_agent_1.LOGOUT_REASON.API_ERROR : event_agent_1.LOGOUT_REASON.USER;
            try {
                const currRoute = this.getCurrentRoute();
                localStorage.removeItem(AUTH_STORAGE);
                sessionStorage.setItem(AUTH_REDIRECT_STORAGE, currRoute);
                this.sendEvent(event_agent_1.EVENT_ENTITY.LOGOUT, { reason, error });
                yield this.authService.signoutRedirect();
            }
            catch (error) {
                this.subject.error({ origin: '[CAS logout]', error });
            }
        });
    }
    getCurrentRoute() {
        return this.authRoutes.indexOf(location.pathname) < 0
            ? `${location.pathname}${location.search}`
            : AUTH_DEFAULT_ROUTE;
    }
    getEventData() {
        var _a, _b, _c, _d, _e, _f, _g;
        return __awaiter(this, void 0, void 0, function* () {
            const user = yield this.authService.getUser();
            if (!user)
                return;
            return {
                appName: this.config.appName,
                at: sha256_1.default(user.access_token).toString(),
                clientId: (_a = user.profile) === null || _a === void 0 ? void 0 : _a.client_id,
                exp: user.expires_at,
                iat: (_b = user.profile) === null || _b === void 0 ? void 0 : _b.auth_time,
                isAdmin: !!((_c = user.profile) === null || _c === void 0 ? void 0 : _c.adminId),
                isAttendant: !!((_d = user.profile) === null || _d === void 0 ? void 0 : _d.attendantId),
                referer: window.location.origin,
                route: window.location.pathname + window.location.search,
                sid: (_e = user.profile) === null || _e === void 0 ? void 0 : _e.sid,
                tgt: sha256_1.default((_f = user.profile) === null || _f === void 0 ? void 0 : _f.jti).toString(),
                ucode: (_g = user.profile) === null || _g === void 0 ? void 0 : _g.ucode
            };
        });
    }
    sendEvent(entity, payload) {
        return __awaiter(this, void 0, void 0, function* () {
            const event = yield this.getEventData();
            if (!event)
                return;
            event_agent_1.sendDatahub(entity, Object.assign(Object.assign({}, event), payload));
        });
    }
}
exports.default = Bootstrap;
